/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from 'react-query';

// removed ColumnDef due to type instantiation issues
import { useDeleteMemberById } from '@/api/workspaces/useDeleteAudienceMember';
import { useAppSelector } from '@/app/hooks';
import Modal from '@/component-library/primitives/Modal/Modal';
import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
// removed permission checker imports as actions menu is removed
import queryKeys from '@/modules/shared/query/queryKeys';

import i18n from '../../../i18n';

/** Higher = more engaged; unknown/missing sort last via NaN handling in sortingFn. */
const engagementRank = (raw: unknown): number => {
  if (raw == null || raw === '-') return NaN;
  const map: Record<string, number> = {
    high: 4,
    medium: 3,
    low: 2,
    inactive: 1,
  };
  return map[String(raw).toLowerCase()] ?? NaN;
};

export const useAudienceTable = (options?: { serverDrivenSort?: boolean }) => {
  const serverDrivenSort = options?.serverDrivenSort ?? false;
  const t = (key: string) => (i18n as any).t(key) as string;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteMemberMutation = useDeleteMemberById();
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const stripeFinancial = useStripeFinancialFeaturesEnabled();

  const isActionAllowed = true; //usePermissionChecker(PermissionsEnum.AUDIENCE, HttpMethodsEnum.PUT);

  const handleDeleteModal = (state: boolean) => {
    // @ts-ignore
    if (!document.startViewTransition) {
      setShowDeleteModal(state);
    } else {
      // @ts-ignore
      document.startViewTransition(() => {
        flushSync(() => {
          setShowDeleteModal(state);
        });
      });
    }
  };

  // Row-level actions removed (three-dot menu)

  const handleConfirmDelete = () => {
    if (deletingMemberId) {
      deleteMemberMutation.mutate(deletingMemberId, {
        onSuccess: () => {
          setTimeout(() => {
            (queryClient as any).setQueryData(
              [queryKeys.users, workspace?.id],
              (prevData: any[]) => prevData?.filter((member: any) => member.id !== deletingMemberId) || [],
            );
          }, 1000);
        },
        onError: () => {
          console.log('Error deleting member');
        },
        onSettled: () => {
          setDeletingMemberId(null);
        },
      });
    }
    handleDeleteModal(false);
  };

  // No dropdown state/effects since row actions are removed

  const columns = useMemo((): any[] => {
    const stripeSubsColumn = {
      id: 'stripeActiveSubscriptions',
      accessorFn: (row: any) => row.stripeActiveSubscriptions ?? 0,
      header: () => <div>Active Subscriptions</div>,
      enableSorting: false,
      cell: (info: any) => {
        const count = info.getValue();
        return <span className="font-medium">{count as number}</span>;
      },
    };

    return [
      {
        accessorKey: 'name',
        enableSorting: false,
        header: () => <div>{t('AudiencePage.nameColumn')}</div>,
        cell: (info: any) => <span>{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'email',
        enableSorting: serverDrivenSort,
        header: () => <div>{t('AudiencePage.emailColumn')}</div>,
        cell: (info: any) => <div className="max-w-[300px] truncate">{info.getValue() as string}</div>,
        size: 300,
      },
      ...(stripeFinancial ? [stripeSubsColumn] : []),
      {
        id: 'riskLevel',
        accessorFn: (row: any) => {
          const riskScore = row.parsedChurnRiskData?.riskScore ?? 0;
          if (riskScore >= 60) return 'high';
          if (riskScore >= 30) return 'medium';
          return 'low';
        },
        enableSorting: false,
        header: () => <div>{t('AudiencePage.riskLevelColumn')}</div>,
        cell: (info: any) => {
          const riskLevel = info.getValue();
          const getRiskColor = (level: string) => {
            switch (level.toLowerCase()) {
              case 'low':
                return 'text-green-600';
              case 'medium':
                return 'text-yellow-600';
              case 'high':
                return 'text-red-600';
              default:
                return 'text-gray-500';
            }
          };
          return <span className={`font-medium ${getRiskColor(riskLevel as string)}`}>{riskLevel as string}</span>;
        },
      },
      {
        id: 'riskScore',
        accessorFn: (row: any) => row.parsedChurnRiskData?.riskScore,
        sortUndefined: 'last',
        header: () => <div>{t('AudiencePage.riskScoreColumn')}</div>,
        cell: (info: any) => {
          const score = info.getValue() as number | undefined;
          if (score == null || Number.isNaN(score)) return <span>-</span>;
          const getScoreColor = (s: number) => {
            if (s >= 60) return 'text-red-600';
            if (s >= 30) return 'text-yellow-600';
            return 'text-green-600';
          };
          return <span className={`font-medium ${getScoreColor(score)}`}>{score}%</span>;
        },
      },
      {
        id: 'engagementLevel',
        accessorFn: (row: any) => row.parsedChurnRiskData?.engagementLevel ?? '-',
        sortingFn: (rowA: any, rowB: any, columnId: string) => {
          const a = engagementRank(rowA.getValue(columnId));
          const b = engagementRank(rowB.getValue(columnId));
          if (Number.isNaN(a) && Number.isNaN(b)) return 0;
          if (Number.isNaN(a)) return 1;
          if (Number.isNaN(b)) return -1;
          return a - b;
        },
        header: () => <div>Engagement</div>,
        cell: (info: any) => {
          const level = info.getValue() as string;
          if (level === '-') return <span>-</span>;
          const getEngagementColor = (lvl: string) => {
            switch (lvl.toLowerCase()) {
              case 'high':
                return 'text-green-600';
              case 'medium':
                return 'text-yellow-600';
              case 'low':
                return 'text-yellow-600';
              case 'inactive':
                return 'text-red-600';
              default:
                return 'text-gray-500';
            }
          };
          return <span className={`font-medium ${getEngagementColor(level)}`}>{level}</span>;
        },
      },
      {
        id: 'createdAt',
        accessorFn: (row: any) => {
          if (stripeFinancial && row.stripeCreatedAt) {
            return new Date(row.stripeCreatedAt * 1000).toISOString();
          }
          return row.createdAt;
        },
        enableSorting: false,
        header: () => <div>Signed Up</div>,
        cell: (info: any) => {
          const dateString = info.getValue() as string;
          const date = new Date(dateString);

          return (
            <div className="flex flex-col">
              <span>
                {date.toLocaleDateString('en-us', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <span className="text-sm">
                {date.toLocaleTimeString('en-us', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          );
        },
      },
    ];
  }, [stripeFinancial, t, serverDrivenSort]);

  const deleteConfirmationModal: React.ReactElement = (
    <Modal
      isOpen={showDeleteModal}
      title={t('AudiencePage.modalTitle')}
      body={t('AudiencePage.modalBody')}
      cancelBtnTxt={t('AudiencePage.modalCancelBtn')}
      confirmBtnTxt={t('AudiencePage.modalConfirmBtn')}
      onCancel={() => handleDeleteModal(false)}
      onConfirm={handleConfirmDelete}
    />
  );

  return { columns, deleteConfirmationModal };
};
